import { MessageBar, MessageBarType, Text } from '@fluentui/react';
import { Tag } from '@fluentui/react-components';
import { Fire24Regular, Info24Regular, Warning24Regular } from '@fluentui/react-icons';
import {
  AnnouncementBasicResponse,
  AnnouncementResponse,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { Announcement as AnnouncementType } from '@mnspro-cloud/teams-services';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialIcon } from '../../IconPicker/IconRegistry';
import './Announcement.scss';

interface AnnouncementProps {
  announcement:
    | AnnouncementType
    | AnnouncementResponse
    | AnnouncementBasicResponse
    | Omit<AnnouncementResponse, 'limit' | 'owner' | 'id'>;
  className?: string;
}

const MAX_LENGTH = 300;
const truncateText = (text: string | undefined | null) => {
  if (!text || text.length <= MAX_LENGTH) return text || '';
  return text.slice(0, MAX_LENGTH) + '...';
};

const severityIcons = {
  Info: <Info24Regular className="info" />,
  Warning: <Warning24Regular className="warning" />,
  Important: <Fire24Regular className="important" />,
};

export const Announcement: React.FC<AnnouncementProps> = ({ announcement, className }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const { title, description, text, severity = 'Info', isEnabled = true, symbol } = announcement;

  const groupsAndRoles = useMemo(() => {
    const recipients =
      'recipients' in announcement ? announcement.recipients : { roles: [], groups: [] };

    return [
      ...recipients.roles.map((role: { name: string }) => role.name),
      ...recipients.groups.map((group: { displayName: string }) => group.displayName),
    ];
  }, [announcement]);

  if (!isEnabled) return null;

  const displayDescription = isExpanded ? description || '' : truncateText(description);
  const hasMore = (description?.length || 0) > MAX_LENGTH || (text && text.length > 0);

  return (
    <MessageBar
      className={cn('announcement', className, 'announcement-' + severity.toLowerCase())}
      messageBarType={MessageBarType[severity.toLowerCase() as keyof typeof MessageBarType]}
      dismissButtonAriaLabel={t('dashboard:announcements:read_more', { defaultValue: 'Read more' })}
      onDismiss={hasMore ? () => setIsExpanded(!isExpanded) : undefined}
    >
      <div className="announcementIcon">
        {symbol ? (
          <MaterialIcon name={symbol} />
        ) : (
          severityIcons[severity as keyof typeof severityIcons]
        )}
      </div>
      <div className="announcementContent">
        <div className="announcementText">
          <Text variant="large" className="announcementTitle">
            {title}
          </Text>
          {groupsAndRoles.length > 0 && (
            <div className="announcementGroupsAndRoles">
              {groupsAndRoles?.map((name, index) => (
                <Tag key={index} className="announcementGroupOrRole">
                  {name}
                </Tag>
              ))}
            </div>
          )}
          <div
            className="announcementDescription"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(displayDescription) }}
          />
          {isExpanded && text && (
            <>
              <h3>
                {t('dashboard:announcements:fullText', {
                  defaultValue: 'Weitere Informationen:',
                })}
              </h3>
              <div
                className="announcement-full-text"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
              />
            </>
          )}
          {hasMore && (
            <Text
              variant="small"
              className="announcementShowMore"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded
                ? t('dashboard:announcements:show_less', { defaultValue: 'Weniger anzeigen' })
                : t('dashboard:announcements:show_more', { defaultValue: 'Mehr anzeigen' })}
            </Text>
          )}
        </div>
      </div>
    </MessageBar>
  );
};

export default Announcement;
